import { createColumnHelper } from '@tanstack/react-table';
import { AgentProfilePictureByLogin } from 'components/AgentProfilePictureByLogin';
import { FlexTableV2 } from 'components/FlexTableV2';
import { useMonitoringUnified } from 'hooks/useMonitoringUnified';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MonitoringUnifiedAgentsChatAndPhoneBoolean,
  MonitoringUnifiedOnlyChat,
  MonitoringUnifiedOnlyPhone
} from './CustomCells';
import {
  MonitoringUnifiedAgentNameWrapper,
  MonitoringUnifiedQueuesActiveAndReceivedCalls,
  MonitoringUnifiedQueuesChatAndPhone,
  MonitoringUnifiedQueuesTooltipChatAndTelephone
} from './CustomCells/index';
import { MonitoringUnifiedOnlyProtocol } from './CustomCells';
import { MonitoringUnifiedExtensionTag } from './CustomCells';

const columnHelper = createColumnHelper();

const queueColumns = [
  columnHelper.accessor('name', {
    header: 'Nome',
    cell: (cell) => (
      // Estou utilizando o MonitoringUnifiedAgentNameWrapper porque nesse momento a estilização é igual. Se precisar ajustar, pode mudar.
      <MonitoringUnifiedAgentNameWrapper>
        {cell.getValue()}
      </MonitoringUnifiedAgentNameWrapper>
    )
  }),
  // Métricas de Tempo
  columnHelper.accessor('averageAttendanceTime', {
    header: 'TMA',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageWaitingTime', {
    header: 'TME',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageResponseTimeAfterAssign', {
    header: 'TMR Após Atribuição',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('averageResponseTimeAfterAnswer', {
    header: 'TMR Após Atendimento',
    cell: MonitoringUnifiedOnlyChat
  }),
  // Status da Fila
  columnHelper.accessor('inAttendance', {
    header: 'Em atendimento',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('inAssigned', {
    header: 'Conversas Atribuídas',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('inWaitInQueue', {
    header: 'Em espera com distribuição',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('inWaitWithoutQueue', {
    header: 'Em espera sem distribuição',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('closedChats', {
    header: 'Conversas Encerradas',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('telephonyCalls', {
    header: 'Chamadas Tel.',
    cell: MonitoringUnifiedQueuesActiveAndReceivedCalls
  }),
  // Métricas de Agentes
  columnHelper.accessor('loggedAgents', {
    header: 'At. Logados',
    cell: MonitoringUnifiedQueuesTooltipChatAndTelephone
  }),
  columnHelper.accessor('freeAgents', {
    header: 'At. Livres',
    cell: MonitoringUnifiedQueuesTooltipChatAndTelephone
  }),
  columnHelper.accessor('pausedAgents', {
    header: 'At. Pausados',
    cell: MonitoringUnifiedQueuesTooltipChatAndTelephone
  }),
  // Satisfação
  columnHelper.accessor('averageSatisfaction', {
    header: 'CSAT',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageNps', {
    header: 'NPS',
    cell: MonitoringUnifiedQueuesChatAndPhone
  })
];

export const agentColumns = [
  columnHelper.accessor('name', {
    header: 'Nome',
    cell: (cell) => (
      <MonitoringUnifiedAgentNameWrapper>
        <AgentProfilePictureByLogin
          style={{
            width: '1.5lh',
            height: '1.5lh',
            borderRadius: '100%',
            marginRight: '.5rem'
          }}
          login={cell.row.original.login}
        />
        {cell.getValue()}
      </MonitoringUnifiedAgentNameWrapper>
    )
  }),
  // Métricas de Tempo
  columnHelper.accessor('averageAttendanceTime', {
    header: 'TMA',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageWaitingTime', {
    header: 'TME',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageResponseTimeAfterAssign', {
    header: 'TMR Após Atribuição',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('averageResponseTimeAfterAnswer', {
    header: 'TMR Após Atendimento',
    cell: MonitoringUnifiedOnlyChat
  }),
  // Status do Agente
  columnHelper.accessor('inPause', {
    header: 'Em pausa',
    cell: MonitoringUnifiedAgentsChatAndPhoneBoolean
  }),
  columnHelper.accessor('inAttendance', {
    header: 'Conversas em Atendimento',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('inAssigned', {
    header: 'Conversas Atribuídas',
    cell: MonitoringUnifiedOnlyChat
  }),
  // Desempenho
  columnHelper.accessor('closedChats', {
    header: 'Conversas Encerradas',
    cell: MonitoringUnifiedOnlyChat
  }),
  columnHelper.accessor('telephonyCalls', {
    header: 'Chamadas Tel.',
    cell: MonitoringUnifiedQueuesActiveAndReceivedCalls
  }),
  columnHelper.accessor('countProtocol', {
    header: 'Protocolos abertos',
    cell: MonitoringUnifiedOnlyProtocol
  }),
  // Informações Adicionais
  columnHelper.accessor('extensionPhone', {
    header: 'Ramal',
    cell: (cell) => {
      return cell.getValue() ? (
        <MonitoringUnifiedExtensionTag>
          {cell.getValue()}
        </MonitoringUnifiedExtensionTag>
      ) : (
        '-'
      );
    }
  }),
  // Satisfação
  columnHelper.accessor('averageSatisfaction', {
    header: 'CSAT',
    cell: MonitoringUnifiedQueuesChatAndPhone
  }),
  columnHelper.accessor('averageNps', {
    header: 'NPS',
    cell: MonitoringUnifiedQueuesChatAndPhone
  })
];

export function UnifiedMonitoring() {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [columnSizingAgents, setColumnSizingAgents] = useState();
  const [columnSizingQueues, setColumnSizingQueues] = useState();
  const [columnVisibilityAgents, setColumnVisibilityAgents] = useState();
  const [columnVisibilityQueues, setColumnVisibilityQueues] = useState();

  const { monitoringQueueTableData, monitoringAgentTableData } =
    useMonitoringUnified();

  return (
    <main
      style={{
        margin: '1rem',
        padding: '1rem',
        backgroundColor: 'white',
        borderRadius: '.25rem'
      }}
    >
      <div
        style={{
          backgroundColor: systemConfiguration.primary_color,
          padding: '1rem 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          borderRadius: '.25rem',
          color: 'white'
        }}
      >
        <h2
          style={{
            color: 'inherit',
            marginBottom: 'unset',
            textTrim: '0',
            lineHeight: '1em'
          }}
        >
          Monitoramento Unificado
        </h2>
      </div>

      <div
        style={{
          backgroundColor: systemConfiguration.secondary_color,
          padding: '.5rem .5rem',
          display: 'flex',
          justifyContent: 'space-between',
          borderRadius: '.25rem',
          color: 'white',
          marginTop: '2rem'
        }}
      >
        <h3
          style={{
            color: 'inherit',
            marginBottom: 'unset',
            textTrim: '0',
            lineHeight: '1em'
          }}
        >
          Filas
        </h3>
      </div>
      <FlexTableV2
        table={{
          columns: queueColumns,
          data: monitoringQueueTableData,
          enableLocalStorageMemoWithKey: 'true',
          columnResizeMode: 'onChange',
          enableLocalStorageMemo: true
        }}
        state={{
          setColumnSizing: setColumnSizingQueues,
          columnVisibility: columnVisibilityQueues,
          setColumnVisibility: setColumnVisibilityQueues
        }}
      />

      <div
        style={{
          backgroundColor: systemConfiguration.secondary_color,
          padding: '.5rem .5rem',
          display: 'flex',
          justifyContent: 'space-between',
          borderRadius: '.25rem',
          color: 'white',
          marginTop: '2rem'
        }}
      >
        <h3
          style={{
            color: 'inherit',
            marginBottom: 'unset',
            textTrim: '0',
            lineHeight: '1em'
          }}
        >
          Atendentes
        </h3>
      </div>
      <FlexTableV2
        table={{
          columns: agentColumns,
          data: monitoringAgentTableData,
          enableLocalStorageMemoWithKey: 'true',
          columnResizeMode: 'onChange',
          enableLocalStorageMemo: true
        }}
        state={{
          setColumnSizing: setColumnSizingAgents,
          columnVisibility: columnVisibilityAgents,
          setColumnVisibility: setColumnVisibilityAgents
        }}
      />
    </main>
  );
}
